/**
 * ConnectionSelectorDialog - popup for selecting an active connection
 * Used when toolbar actions need a connection but multiple are available
 */

import { useEffect, useRef, useState } from 'react';
import { Check, Server, Settings2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import type { ActiveConnection } from '../../connections/models/connectionTypes';
import { safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

type ConnectionSelectorDialogProps = {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when dialog is closed without selection */
  onOpenChange: (open: boolean) => void;
  /** List of active connections to choose from */
  connections: ActiveConnection[];
  /** Called when a connection is selected */
  onSelect: (connectionId: string) => void;
  /** Called when "Manage Connections" is clicked */
  onManageConnections?: () => void;
  /** Explorer's suggested connection ID (pre-selected in list) */
  suggestedConnectionId?: string | null;
};

export function ConnectionSelectorDialog({
  open,
  onOpenChange,
  connections,
  onSelect,
  onManageConnections,
  suggestedConnectionId,
}: ConnectionSelectorDialogProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Reset selection when dialog opens, pre-selecting suggested connection
  useEffect(() => {
    if (open) {
      if (suggestedConnectionId) {
        const idx = connections.findIndex((c) => c.id === suggestedConnectionId);
        setSelectedIndex(idx >= 0 ? idx : 0);
      } else {
        setSelectedIndex(0);
      }
    }
  }, [open, suggestedConnectionId, connections]);

  // Scroll selected item into view
  useEffect(() => {
    if (open && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [selectedIndex, open]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, connections.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (connections[selectedIndex]) {
          onSelect(connections[selectedIndex].id);
        }
        break;
    }
  };

  const handleSelect = (connectionId: string) => {
    onSelect(connectionId);
  };

  const handleManageConnections = () => {
    onOpenChange(false);
    onManageConnections?.();
  };

  const getConnectionDisplayName = (conn: ActiveConnection): string => {
    return safeConnectionName(conn.connection);
  };

  const getConnectionStatusIndicator = (conn: ActiveConnection) => {
    if (conn.status === 'connected') {
      return (
        <span
          className="inline-block w-2 h-2 rounded-full bg-green-500"
          title="Connected"
          data-testid="connection-status-connected"
        />
      );
    }
    return (
      <span
        className="inline-block w-2 h-2 rounded-full bg-yellow-500"
        title="Connecting"
        data-testid="connection-status-connecting"
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[400px]"
        data-testid="connection-selector-dialog"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle>Select Connection</DialogTitle>
          <DialogDescription>
            Choose which connection to use for this action
          </DialogDescription>
        </DialogHeader>

        <div
          ref={listRef}
          className="py-2 max-h-[300px] overflow-y-auto"
          role="listbox"
          aria-label="Available connections"
        >
          {connections.map((conn, index) => (
            <button
              key={conn.id}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              type="button"
              role="option"
              aria-selected={index === selectedIndex}
              onClick={() => handleSelect(conn.id)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring/30',
                index === selectedIndex
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent/50'
              )}
              data-testid={`connection-option-${conn.id}`}
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted">
                <Server className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">
                    {getConnectionDisplayName(conn)}
                  </span>
                  {getConnectionStatusIndicator(conn)}
                </div>
                <span className="text-sm text-muted-foreground truncate block">
                  {safeConnectionServer(conn.connection)}
                </span>
              </div>
              {index === selectedIndex && (
                <Check className="h-4 w-4 text-primary shrink-0" />
              )}
            </button>
          ))}
        </div>

        {onManageConnections && (
          <div className="pt-2 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={handleManageConnections}
              data-testid="manage-connections-link"
            >
              <Settings2 className="h-4 w-4 mr-2" />
              Manage Connections...
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
